import type { KeyboardEvent } from 'react'

/**
 * AN ENTER THAT SENDS, in a box where Enter sends and Shift+Enter is a new line (the note
 * sheet, a tab's box in More › Chrome tabs).
 *
 * Not an Enter that is choosing a word in an input method — Zhuyin, Pinyin, Kana. Chrome
 * marks that keydown `isComposing`; WebKit (Safari, and the WKWebView the Mac and iOS apps
 * run in) sends it AFTER compositionend with isComposing false and keyCode 229, so a guard
 * on isComposing alone sent half a sentence in the Mac app (the review of 2026-10-14).
 */
export function enterSends(e: KeyboardEvent): boolean {
  return e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.nativeEvent.keyCode !== 229 && !e.defaultPrevented
}

/**
 * Take the key for this box alone. App listens on the document for Cmd/Ctrl+Enter — the
 * composer's send chord — and submits for whichever agent is open on the stage; a sheet
 * portaled into <body> sits on that path, so one Cmd+Enter in it sent here AND submitted
 * there (a bare Enter into another agent's terminal, answering whatever it was asking).
 * App now also leaves alone any key pressed inside an open sheet (inOpenSheet, below) —
 * the chords that do not send, and focus on the sheet's buttons — so this is the box's
 * own belt to that.
 */
export function keepEnter(e: KeyboardEvent): void {
  e.preventDefault()
  e.stopPropagation()
}

/**
 * A key pressed inside an open sheet (every Sheet is aria-modal) is that sheet's: App's
 * Cmd/Ctrl+Enter must not submit for the agent on the stage behind it — Shift+Cmd+Enter in
 * a sheet's box, or one pressed with focus on a sheet's button (the review of 2026-10-14).
 */
export function inOpenSheet(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[aria-modal="true"]') !== null
}
